import type { TodayView } from '@aj/core';
import { Button, Card, HStack, Text, VStack } from '@aj/ui';
import { useState } from 'react';

import { useT } from '../../providers/I18nProvider.js';
import { Compose } from './Compose.js';
import { TaskList } from './TaskList.js';

export type TasksCardProps = {
  tasks: TodayView['tasks'];
  onAdd: (name: string) => void;
  onToggle: (taskId: string) => void;
  className?: string;
  padding?: 4 | 5;
};

/**
 * Today's work, beside the habits rather than instead of them.
 *
 * Habits are the shape of the day and tasks are its content — a student can
 * tick every habit and still not have studied, which is exactly the gap this
 * closes. Both write to the same day, so both feed the same Power Level.
 *
 * One card, one list, one field. No due-date picker: a task typed here is
 * today's, and dating one is a thing to add when someone asks for it.
 */
export function TasksCard({ tasks, onAdd, onToggle, className, padding = 4 }: TasksCardProps) {
  const t = useT();
  const [composing, setComposing] = useState(false);

  const open = tasks.items.filter((task) => !task.done).length + tasks.hiddenCount;

  return (
    <Card padding={padding} className={className}>
      <VStack gap={3}>
        <HStack gap={3} align="baseline" justify="between">
          <Text variant="overline" tone="subtle">
            {t('home.tasks')}
          </Text>
          {open > 0 && (
            <Text variant="caption" tone="muted" numeric>
              {t('home.openTasks', { count: open })}
            </Text>
          )}
        </HStack>

        {tasks.items.length === 0 ? (
          <Text variant="caption" tone="subtle">
            {t('home.noTasks')}
          </Text>
        ) : (
          <TaskList
            tasks={tasks.items}
            hiddenCount={tasks.hiddenCount}
            label={t('home.tasks')}
            onToggle={onToggle}
          />
        )}

        {/* Stays open after a submit — a study plan arrives three lines at a
            time, not one. */}
        {composing ? (
          <Compose
            labelKey="home.taskName"
            actionKey="home.addTask"
            onSubmit={onAdd}
            onDismiss={() => setComposing(false)}
          />
        ) : (
          <Button
            variant="secondary"
            icon="plus"
            onClick={() => setComposing(true)}
            className="self-start"
          >
            {t('home.addTask')}
          </Button>
        )}
      </VStack>
    </Card>
  );
}
